// helpers.ts
import { CalculationProcessStep, Co2ReferenceObject } from 'store/types/assembler'
import { iInterpreterPayloadResult } from 'store/types/interpreter'
import Papa from "papaparse";
import * as XLSX from "xlsx";

export function parsePastedText(input: string): string[][] {
    return input
      .trim()
      .split("\n")
      .map(row => row.split("\t").map(cell => cell.trim()));
  }

export function parseCSV(file: File): Promise<string[][]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      complete: (results: any) => {
        resolve(results.data as string[][]);
      },
      error: reject,
    });
  });
}

export function parseXLSX(file: File): Promise<string[][]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event: any) => {
      const workbook = XLSX.read(event.target.result, { type: "binary" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const data: string[][] = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
      resolve(data);
    };
    reader.onerror = reject;
    reader.readAsBinaryString(file);
  });
}

export function formatForAI(table: string[][]): string {
  const header = "Processed BOM Table:\n";
  const formatted = table.map(row => row.join(" | ")).join("\n");
  return `${header}${formatted}`;
}

  export function mapTableToObjects(table: string[][]): Record<string, string>[] {
    if (!table || table.length < 2) return []
  
    const headers = table[0].map(h => h.trim())
    return table.slice(1).map(row => {
      const obj: Record<string, string> = {}
      headers.forEach((key, index) => {
        obj[key] = row[index] ?? ''
      })
      return obj
    })
  }



export function convertInterpreterDataToCalculationProcessChain(
  data: iInterpreterPayloadResult[]
): CalculationProcessStep[] {
  const grouped = new Map<
    string,
    { matched: Co2ReferenceObject[]; unmatched: Co2ReferenceObject[] }
  >();

  data.forEach((item) => {
    const stepName = item.calculationStepName || "Undefined";

    if (!grouped.has(stepName)) {
      grouped.set(stepName, { matched: [], unmatched: [] });
    }

    const stepBucket = grouped.get(stepName)!;

    // Case 1: Matched item (with sid + contextName)
    if (item.sid && item.contextData?.contextName) {
      const rawParams = item.params as Record<string, any>;
      const filteredParams: Record<string, any> = {};
      const keysToKeep = ["yield", "netWeight", "countryCode", "distance"];

      keysToKeep.forEach((key) => {
        if (rawParams[key] !== undefined) {
          filteredParams[key] = rawParams[key];
        }
      });

      const co2Object: Co2ReferenceObject = {
        name: item.customName,
        sid: item.sid,
        context: item.contextData.contextName,
        params: filteredParams,
      } as Co2ReferenceObject;

      stepBucket.matched.push(co2Object);
    }
    // Case 2: Unmatched item (no sid or empty context)
    else {
      const netWeight = item.params?.netWeight ?? null;
      const unmatched: Co2ReferenceObject = {
        sid: undefined,
        name: item.customName,
        context: "materials.other",
        params: {
          netWeight,
          customFootprint: 0,
        },
      } as Co2ReferenceObject;

      stepBucket.unmatched.push(unmatched);
    }
  });

  // Build final CalculationProcessStep[]
  const result: CalculationProcessStep[] = Array.from(grouped.entries()).map(
    ([stepName, { matched, unmatched }], index) => ({
      name: stepName,
      position: index + 1,
      subAssemblies: [],
      sustamizeTargets: mapToTargetByContext(matched),
      customTargets: {
        materials: {
          other: unmatched,
        },
      },
    })
  );

  return result;
}


function mapToTargetByContext(items: Co2ReferenceObject[]) {
  const contextMap: { [key: string]: Co2ReferenceObject[] } = {}

  items.forEach((item) => {
    const ctx = item.context || ''
    if (!contextMap[ctx]) contextMap[ctx] = []
    contextMap[ctx].push(item)
  })

  const target = {
    materials: {
      metals: contextMap['.materials.metals'] ?? [],
      elements: contextMap['.materials.elements'] ?? [],
      polymers: contextMap['.materials.polymers'] ?? [],
      sinters: contextMap['.materials.sinters'] ?? [],
      composites: contextMap['.materials.composites'] ?? [],
    },
    logistics: {
      packaging: contextMap['.logistics.packaging'] ?? [],
      transportation: contextMap['.logistics.transportation'] ?? [],
    },
    production: {
      manufacturing: contextMap['.production.manufacturing'] ?? [],
    },
    components: {
      electronics: contextMap['.electronics'] ?? [],
    },
  }

  return target
}
